import { queryOptions } from '@tanstack/react-query';
import { YVES_API_URL } from '@frodo/lib/services/http/shared/constants';
import ApiHttpClientService from '@frodo/lib/services/http/base/api-http-client.service';
import { MUTATION_KEYS, QUERY_KEYS } from '@frodo/lib/services/http/shared/queryKeys';
import { CartResponse, ActionResponse } from '@frodo/models/types';
import { mutationOptions } from '@frodo/lib/utils/client';

type CartItemQuantityUpdateProps = {
  sku: string;
  quantity: number;
  isBundle?: boolean;
};

class ApiCartClientService extends ApiHttpClientService {
  public async getCart(): Promise<CartResponse> {
    return this.get<CartResponse>(YVES_API_URL.cart);
  }

  public async updateCartItemQuantity({
    sku,
    quantity,
  }: CartItemQuantityUpdateProps) {
    const response: ActionResponse = await this.post(
      YVES_API_URL.updateCartItemQuantity(sku),
      undefined,
      { params: { quantity } },
    );

    return response;
  }

  public async removeCartItem(sku: string) {
    const response: ActionResponse = await this.post(
      YVES_API_URL.removeCartItem(sku),
    );

    return response;
  }

  public async addItemToCart({
    sku,
    quantity,
    isBundle,
  }: CartItemQuantityUpdateProps) {
    const response: ActionResponse = await this.post(
      YVES_API_URL.addItemToCart,
      {
        sku,
        quantity,
        isBundle,
      },
    );

    return response;
  }
}

export const apiCartClientService = new ApiCartClientService();

export const cartQuery = () => {
  return queryOptions({
    queryKey: QUERY_KEYS.cart(),
    queryFn: () => apiCartClientService.getCart(),
  });
};

const CART_MUTATION_SCOPE = { id: 'cart' } as const;

export const updateCartItemQuantityMutation = () => {
  return mutationOptions<ActionResponse, unknown, CartItemQuantityUpdateProps>({
    mutationKey: MUTATION_KEYS.cartItemQuantityUpdate(),
    mutationFn: ({ sku, quantity }: CartItemQuantityUpdateProps) =>
      apiCartClientService.updateCartItemQuantity({ sku, quantity }),
    scope: CART_MUTATION_SCOPE,
  });
};

export const removeCartItemMutation = () => {
  return mutationOptions<ActionResponse, unknown, { sku: string }>({
    mutationKey: MUTATION_KEYS.cartItemRemove(),
    mutationFn: ({ sku }: { sku: string }) =>
      apiCartClientService.removeCartItem(sku),
    scope: CART_MUTATION_SCOPE,
  });
};

export const addItemToCartMutation = () => {
  return mutationOptions<ActionResponse, unknown, CartItemQuantityUpdateProps>({
    mutationKey: MUTATION_KEYS.cartItemAdd(),
    mutationFn: ({
      sku,
      quantity,
      isBundle = false,
    }: CartItemQuantityUpdateProps) =>
      apiCartClientService.addItemToCart({ sku, quantity, isBundle }),
    scope: CART_MUTATION_SCOPE,
  });
};

export const deletedCartItemQuery = () => {
  return queryOptions({
    queryKey: QUERY_KEYS.deletedCartItem(),
    queryFn: () => ({
      idx: 1,
      url: '',
      name: '',
      sku: '',
    }),
  });
};
